/**
 * Step Handlers Hook - 提取步骤处理逻辑
 */

import { useCallback } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from './services';
import type {
    SmartCopywritingProject,
    BrandBrief,
    CreateProjectRequest,
    CopywritingVersion,
    CopywritingStep,
} from './types';

interface UseStepHandlersProps {
    productName: string;
    sellingPoints: string[];
    keywordsBySite: Record<string, string>;
    competitorTitles: string;
    selectedBrandId?: number;
    selectedBrand?: BrandBrief;
    customAudienceInput: string;
    projectId: number | null;
    project: SmartCopywritingProject | null;
    selectedAudiencesCount: number;
    selectedScenariosCount: number;
    // Callbacks from useSmartCopywriting
    goToStep: (step: CopywritingStep) => void;
    analyzeAudiences: (
        productName: string,
        sellingPoints: string[],
        brandTone?: string,
        customInput?: string,
        project?: any
    ) => Promise<any>;
    recommendScenarios: () => Promise<any>;
    generateCopy: (targetSites: string[], competitorTitles?: string[]) => Promise<any>;
    saveVersion: (notes?: string) => Promise<CopywritingVersion | null>;
    // Callbacks to parent
    onProjectCreated?: (project: SmartCopywritingProject) => void;
    setProjectId: (id: number | null) => void;
}

export const useStepHandlers = ({
    productName,
    sellingPoints,
    keywordsBySite,
    competitorTitles,
    selectedBrandId,
    selectedBrand,
    customAudienceInput,
    projectId,
    project,
    selectedAudiencesCount,
    selectedScenariosCount,
    goToStep,
    analyzeAudiences,
    recommendScenarios,
    generateCopy,
    saveVersion,
    onProjectCreated,
    setProjectId,
}: UseStepHandlersProps) => {
    const { message } = App.useApp();

    // Step 1: 创建项目
    const handleStep1Next = useCallback(async () => {
        const validSellingPoints = sellingPoints.filter(p => p.trim());
        if (!productName.trim() || validSellingPoints.length === 0) {
            message.warning('请填写产品名称和至少一条卖点');
            return;
        }

        try {
            const projectData: CreateProjectRequest = {
                name: `${productName} - 智能文案`,
                product_name: productName,
                selling_points: validSellingPoints,
                keywords_by_site: keywordsBySite,
                brand: selectedBrandId,
                competitor_titles: competitorTitles
                    .split('\n')
                    .map(t => t.trim())
                    .filter(t => t.length > 0),
            };

            const newProject = await smartCopywritingAPI.createProject(projectData);
            setProjectId(newProject.id);
            onProjectCreated?.(newProject);

            goToStep(2);
            message.success('项目创建成功，请开始分析目标人群');
        } catch (error: any) {
            console.error('创建项目失败:', error);
            const errorMsg = error?.response?.data?.error ||
                error?.response?.data?.detail ||
                error?.message ||
                '创建项目失败';
            message.error(errorMsg);
        }
    }, [productName, sellingPoints, keywordsBySite, selectedBrandId, onProjectCreated, message, goToStep, setProjectId]);

    // Step 2: 重新分析人群
    const handleReAnalyze = useCallback(async (customInput?: string) => {
        const validSellingPoints = sellingPoints.filter(p => p.trim());
        if (!productName.trim() || validSellingPoints.length === 0) {
            message.warning('请先完善产品信息（产品名称和卖点）');
            return;
        }

        if (!projectId && !project) {
            message.warning('请先点击第一步的"下一步"创建项目');
            goToStep(1);
            return;
        }

        try {
            const projectContext = project || (projectId ? { id: projectId } as any : undefined);
            await analyzeAudiences(
                productName,
                validSellingPoints,
                selectedBrand?.tone,
                customInput || customAudienceInput,
                projectContext
            );
        } catch (error: any) {
            console.error('人群分析失败:', error);
            const errorMsg = error?.response?.data?.error ||
                error?.response?.data?.detail ||
                error?.message ||
                '人群分析失败';
            message.error(errorMsg);
        }
    }, [productName, sellingPoints, selectedBrand, customAudienceInput, projectId, project, analyzeAudiences, goToStep, message]);

    // Step 2 -> 3: 推荐场景
    const handleStep2Next = useCallback(async () => {
        console.log('handleStep2Next called, selectedAudiencesCount:', selectedAudiencesCount);
        if (selectedAudiencesCount === 0) {
            message.warning('请至少选择一个目标人群');
            return;
        }
        console.log('Calling recommendScenarios...');
        try {
            await recommendScenarios();
            console.log('recommendScenarios completed');
        } catch (e) {
            console.error('recommendScenarios error:', e);
        }
    }, [selectedAudiencesCount, recommendScenarios, message]);

    // Step 3 -> 4: 生成文案
    const handleStep3Next = useCallback(async (targetSites: string[]) => {
        if (selectedScenariosCount === 0) {
            message.warning('请至少选择一个使用场景');
            return;
        }

        if (!targetSites || targetSites.length === 0) {
            message.warning('请至少选择一个目标站点');
            return;
        }

        const competitorTitlesList = competitorTitles
            .split('\n')
            .map(t => t.trim())
            .filter(t => t.length > 0);

        await generateCopy(targetSites, competitorTitlesList);
    }, [selectedScenariosCount, generateCopy, message, competitorTitles]);

    // 保存版本
    const handleSaveVersion = useCallback(async (notes?: string) => {
        const version = await saveVersion(notes);
        if (version) {
            goToStep(5);
        }
    }, [saveVersion, goToStep]);

    return {
        handleStep1Next,
        handleReAnalyze,
        handleStep2Next,
        handleStep3Next,
        handleSaveVersion,
    };
};
